import React, { Fragment } from "react";
import { Link } from "react-router-dom";
import * as S from "sanctuary";
import * as $ from "sanctuary-def";
import {
  PendingIcon,
  VoidedIcon,
  SuccessfulIcon,
  RefundIcon,
  AchReturnIcon,
  ChargebackIcon,
  ChargebackReversalIcon,
  FailedIcon,
  ErroredIcon,
  ShortageIcon,
  OverageIcon,
  ReversalNeededIcon,
  DuplicateIcon,
  RejectedIcon,
  RejectedVelocityIcon,
  CalendarIcon,
  StatusUnknownIcon,
  Box,
  Cluster,
  Text,
  ButtonWithAction,
  Stack,
  Modal,
} from "@thecb/components";
import connectUserPermissions from "@/components/currentUser/CurrentUserPermissions";
import { PAYMENT_STATUSES, REFUND_TYPES } from "@/sanctuary/typedefs";
import {
  PAYMENT_MODIFICATION_TYPES,
  PAYMENT_INITIATOR_SCHEDULE_SERVICE,
} from "../../Payment.state";
import { BACKGROUND_DIM } from "@/constants/colors";
import Tooltip from "@/components/generic/Tooltip";
import { formatRole } from "@/util/common";
import DownloadReceiptButton from "./DownloadReceiptButton";

const autopayText = "Autopay";

const enabledAssociatedLinks = {
  [PAYMENT_STATUSES.SUCCESSFUL]: "Original payment",
  [PAYMENT_STATUSES.CHARGEBACK]: "Chargeback",
  [PAYMENT_STATUSES.CHARGEBACK_REVERSAL]: "Chargeback Reversal",
  [PAYMENT_STATUSES.ACH_RETURN]: "ACH Return",
};

const textAndIconByStatus = {
  [PAYMENT_STATUSES.PENDING]: {
    Icon: () => <PendingIcon />,
    text: "Transaction Pending",
  },
  [PAYMENT_STATUSES.VOIDED]: {
    Icon: () => <VoidedIcon />,
    text: "Transaction Voided",
  },
  [PAYMENT_STATUSES.SUCCESSFUL]: {
    Icon: () => <SuccessfulIcon />,
    text: "Transaction Successful",
  },
  [PAYMENT_STATUSES.REFUNDED]: {
    Icon: () => <RefundIcon />,
    text: "Transaction Refund",
  },
  [PAYMENT_STATUSES.ACH_RETURN]: {
    Icon: () => <AchReturnIcon />,
    text: "ACH Return",
  },
  [PAYMENT_STATUSES.CHARGEBACK]: {
    Icon: () => <ChargebackIcon />,
    text: "Chargeback",
  },
  [PAYMENT_STATUSES.CHARGEBACK_REVERSAL]: {
    Icon: () => <ChargebackReversalIcon />,
    text: "Chargeback Reversal",
  },
  [PAYMENT_STATUSES.FAILED]: {
    Icon: () => <FailedIcon />,
    text: "Transaction Failed",
  },
  [PAYMENT_STATUSES.ERRORED]: {
    Icon: () => <ErroredIcon />,
    text: "Transaction Errored",
  },
  [PAYMENT_STATUSES.DUPLICATED]: {
    Icon: () => <DuplicateIcon />,
    text: "Transaction Duplicated",
  },
  [PAYMENT_STATUSES.EXPIRED]: {
    Icon: () => <CalendarIcon />,
    text: "Transaction Expired",
  },
  [PAYMENT_STATUSES.REJECTED]: {
    Icon: () => <RejectedIcon />,
    text: "Transaction Rejected",
  },
  [PAYMENT_STATUSES.REJECTED_VELOCITY]: {
    Icon: () => <RejectedVelocityIcon />,
    text: "Rejected Velocity",
  },
  [PAYMENT_STATUSES.REVERSAL_NEEDED]: {
    Icon: () => <ReversalNeededIcon />,
    text: "Reversal needed",
  },
  [PAYMENT_STATUSES.DEBITED]: {
    Icon: () => <ShortageIcon />,
    text: "Shortage",
  },
  [PAYMENT_STATUSES.CREDITED]: {
    Icon: () => <OverageIcon />,
    text: "Overage",
  },
};

const defaultTextAndIcon = {
  Icon: () => <StatusUnknownIcon />,
  text: "Unknown status",
};

const getPaymentInfo = ({ status, isAutopay }) => {
  const { Icon, text } = S.fromMaybe(defaultTextAndIcon)(
    S.get(S.is($.Object))(status)(textAndIconByStatus)
  );
  return (
    <Cluster justify="flex-start" align="center">
      <Icon />
      <Text variant="p" extraStyles="margin-left: 0.5rem; vertical-align: middle;">
        {isAutopay ? `${autopayText} ` : ""}
        {text}
      </Text>
    </Cluster>
  );
};

const displayTransactionStatusLink = (
  linkedTransactions,
  goToPayment,
  onRefundLinkClick
) => {
  const associatedLinks = linkedTransactions.filter((assoc) =>
    Object.keys(enabledAssociatedLinks).includes(assoc.status?.value)
  );
  const hasAssociatedPayments = associatedLinks.length > 0;
  const hasRefunds =
    linkedTransactions.some((assoc) => assoc.status?.value === "refunded") ?? false;

  return (
    <Fragment>
      {(hasAssociatedPayments || hasRefunds) && (
        <Text extraStyles={"padding: 0px 8px;"}>{"|"}</Text>
      )}
      <Text extraStyles={"font-size: 14px; white-space: pre; line-height: 22px"}>
        {hasAssociatedPayments && "Related transactions: "}
        {(hasAssociatedPayments || hasRefunds) &&
          associatedLinks
            .map((transaction) => ({
              key: transaction.id?.value,
              linkURL: `/payment/${transaction.id?.value}`,
              linkText: enabledAssociatedLinks[transaction.status?.value],
              onLinkClick: () =>
                goToPayment({ transactionId: transaction.id?.value }),
            }))
            .concat(
              hasRefunds
                ? {
                    key: "refund_link",
                    linkURL: {},
                    linkText: hasAssociatedPayments
                      ? "Associated refunds"
                      : "View associated refunds",
                    onLinkClick: onRefundLinkClick,
                  }
                : []
            )
            .map((link, index, links) => {
              const separator = index < links.length - 1 ? ", " : "";
              return (
                <Fragment key={link.key}>
                  <Link to={link.linkURL} onClick={link.onLinkClick}>
                    {link.linkText}
                  </Link>
                  {separator}
                </Fragment>
              );
            })}
      </Text>
    </Fragment>
  );
};

const CommonDetails = ({
  payment,
  linkedTransactions,
  userCanVoidTransaction,
  userCanRefundTransaction,
  refundRequestPending,
  voidRequestPending,
  openPartialRefundContainer,
  openVoidRefundModal,
  closeVoidRefundModal,
  submitVoidRefundModal,
  voidRefundModalOpen,
  goToPayment,
  userRole,
  refundsRef,
  downloadReceipt,
  receiptDownloadPending,
}) => {
  const status = payment.status?.value?.value ?? "";
  const isAutopay =
    payment?.paymentInitiator?.value === PAYMENT_INITIATOR_SCHEDULE_SERVICE;
  const ShowTooltip = ({ show, text, children }) => (
    <Fragment>
      {show ? (
        <Tooltip
          height={"68px"}
          width={"200px"}
          variant="error"
          left
          noIcon
          text={text}
        >
          {children}
        </Tooltip>
      ) : (
        <Fragment>{children}</Fragment>
      )}
    </Fragment>
  );

  const onRefundButtonClicked = () => {
    if (
      (payment.refundTypeAllowed?.value?.value ?? REFUND_TYPES.FULL_AMOUNT) ===
      REFUND_TYPES.FULL_AMOUNT
    ) {
      openVoidRefundModal();
    } else {
      openPartialRefundContainer();
    }
  };

  const onRefundLinkClick = () =>
    refundsRef.current.scrollIntoView({
      behavior: "smooth",
    });

  return (
    <Box padding="1.5rem">
      <Stack justify="space-between" direction="row">
        <Cluster align="center" minHeight="100%" extraStyles={{ flexGrow: 1 }}>
          {getPaymentInfo({ status, isAutopay })}
          {displayTransactionStatusLink(
            linkedTransactions,
            goToPayment,
            onRefundLinkClick
          )}
        </Cluster>
        <DownloadReceiptButton
          status={status}
          isLoading={receiptDownloadPending}
          onDownload={() =>
            downloadReceipt({
              transactionId: S.fromMaybe("")(S.map(S.show)(payment.id)),
            })
          }
        />
        {(S.fromMaybe(false)(payment.refundable) ||
          status === PAYMENT_STATUSES.REFUNDED) && (
          <Fragment>
            <Box padding="0">
              <ShowTooltip
                show={
                  !userCanRefundTransaction || status === PAYMENT_STATUSES.REFUNDED
                }
                text={
                  status === PAYMENT_STATUSES.REFUNDED
                    ? `Can't refund an already refunded transaction.`
                    : `You cannot refund as a ${formatRole(userRole.value.value)}.`
                }
              >
                <ButtonWithAction
                  variant="smallSecondary"
                  text="Refund"
                  action={
                    userCanRefundTransaction ? onRefundButtonClicked : () => {}
                  }
                  extraStyles={`flex-grow: 1; flex-basis: 7.5rem; padding: 0.75rem 1.5rem; border-radius: 4px;
              ${
                (!userCanRefundTransaction ||
                  status === PAYMENT_STATUSES.REFUNDED) &&
                `pointer-events: none;
                 border-color: #959ca8;
                 background-color: ${BACKGROUND_DIM};
                 `
              }`}
                  textExtraStyles={
                    (!userCanRefundTransaction ||
                      status === PAYMENT_STATUSES.REFUNDED) &&
                    `color: #6d717e;`
                  }
                  dataQa="Refund"
                />
              </ShowTooltip>
            </Box>

            <Modal
              modalOpen={voidRefundModalOpen}
              modalHeaderText={`Refund Payment`}
              modalBodyText="Are you sure you want to refund this payment? You cannot undo this
              action."
              continueButtonText="Refund"
              continueAction={() => {
                if (!refundRequestPending) {
                  submitVoidRefundModal(PAYMENT_MODIFICATION_TYPES.REFUND);
                }
              }}
              cancelAction={closeVoidRefundModal}
              noBorder
              customWidth={"496px"}
              isLoading={refundRequestPending}
              buttonExtraStyles={`margin: 0rem; padding: 0.75rem 1.5rem; border-radius: 4px;`}
            />
          </Fragment>
        )}
        {(S.fromMaybe(false)(payment.voidable) ||
          status === PAYMENT_STATUSES.VOIDED) && (
          <Fragment>
            <Box padding="0">
              <ShowTooltip
                show={!userCanVoidTransaction || status === PAYMENT_STATUSES.VOIDED}
                text={
                  status === PAYMENT_STATUSES.VOIDED
                    ? `Can't void an already voided transaction.`
                    : `You cannot void as a ${formatRole(userRole.value.value)}.`
                }
              >
                <ButtonWithAction
                  variant="smallSecondary"
                  text="Void"
                  action={userCanVoidTransaction ? openVoidRefundModal : () => {}}
                  extraStyles={`flex-grow: 1; flex-basis: 7.5rem; padding: 0.75rem 1.5rem; border-radius: 4px;
              ${
                (!userCanVoidTransaction || status === PAYMENT_STATUSES.VOIDED) &&
                `pointer-events: none;
                 border-color: #959ca8;
                 background-color: ${BACKGROUND_DIM};
                 `
              }`}
                  textExtraStyles={
                    (!userCanVoidTransaction ||
                      status === PAYMENT_STATUSES.VOIDED) &&
                    `color: #6d717e;`
                  }
                  dataQa="Void"
                />
              </ShowTooltip>
            </Box>
            <Modal
              modalOpen={voidRefundModalOpen}
              modalHeaderText={`Void Payment`}
              modalBodyText="Are you sure you want to void this payment? You cannot undo this action."
              continueButtonText="Void"
              continueAction={() => {
                if (!voidRequestPending) {
                  submitVoidRefundModal(PAYMENT_MODIFICATION_TYPES.VOID);
                }
              }}
              cancelAction={closeVoidRefundModal}
              noBorder
              customWidth={"496px"}
              isLoading={voidRequestPending}
              buttonExtraStyles={`margin: 0rem; padding: 0.75rem 1.5rem; border-radius: 4px;`}
            />
          </Fragment>
        )}
      </Stack>
    </Box>
  );
};

export default connectUserPermissions(CommonDetails);
